/* eslint-disable no-confusing-arrow */
/* eslint-disable max-len */
import React from 'react';
import {
    Box,
    Button,
    Chip,
    Divider,
    Grid,
    Tooltip,
    Typography
} from '@mui/material';

import {
    Info,
    Paid,
    LocalShipping,
    Construction,
    CardMembership,
    Forest,
    ExpandLess,
    ExpandMore,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as FairTransportEmblem } from '../../../assets/icons/fairtransport-emblem.svg';

import { Paper } from '../../StyledComponents';
import { Numbers, SectionDivider } from '../../CommonComponents';
import EntityDriveLine from './EntityDriveline';

import { useDispatch, useSelector } from '../../../redux/hooks';
import { setSelectedMarketEntity, selectSelectedMarketEntity } from '../../../redux/actions/market';
import { selectTenantProfile } from '../../../redux/actions/tenants';
import DhlDetails from './DhlDetails';
import CapacitySuggestion from './CapacitySuggestion';
import Offer from '../../Offers/Offer';
import OfferChatButton from '../../Offers/OfferChatButton';
import { getFormattedOrderExpiryDate, getPublishedText } from '../../../utils/order';
import { RequirementsMatchesType } from '../../../types/RequirementsType';
import RequirementsSection from '../../Orders/OrderRequirements';
import { isTenantFairtransportMember } from '../../../utils/metadata';

const initialMatchingMetadata = {
    vehicleTypesMatch: [],
    equipmentMatch: [],
    sustainabilityMatch: [],
    licencesCertificatesMatch: [],
    customLicencesCertificatesMatch: [],
    fairtransportMatch: true
};

const filterSustainabilityWithoutFairTransport = (sustainability: string[] | undefined) => (sustainability || []).filter((sust: string) => sust !== 'Fair Transport');

export default function EntityDetails() {
    const dispatch = useDispatch();
    const entity = useSelector(selectSelectedMarketEntity());
    const tenantProfile = useSelector(selectTenantProfile());
    const navigate = useNavigate();
    const [metaDataCollapsed, setMetaDataCollapsed] = React.useState(true);
    const [mustBeFairtransport, setMustBeFairtransport] = React.useState(false);
    const [meetsAllRequirements, setAllRequirementsMet] = React.useState(true);

    const [matchingMetadata, setMatchingMetadata] = React.useState<RequirementsMatchesType>(initialMatchingMetadata);

    const jobTitle = (() => {
        if (!entity) return '';
        if (entity.type === 'CAP' && !entity.isMyJob) return 'Ledig kapacitet';
        if (entity.type === 'CAP' && entity.isMyJob) return 'Min kapacitet';
        if (entity.type === 'LL' && entity.isMyJob) return 'Mitt uppdrag';
        return 'Uppdrag';
    })();

    const handleNavigateToCompanyProfile = React.useCallback(() => {
        if (entity?.tenantId && !entity.isRejected && entity.isCarrier) {
            window.open(`/company-profile/${entity.tenantId}`);
        }
    }, [entity?.tenantId, entity?.isRejected, entity?.isCarrier]);

    const refetch = React.useCallback(() => {
        if (entity?.key) {
            dispatch(setSelectedMarketEntity(entity.key));
        }
    }, [dispatch, entity?.key]);

    const entityMetadataLength = React.useMemo(() => (
        entity ? [
            entity.vehicleTypes,
            entity.equipment,
            filterSustainabilityWithoutFairTransport(entity.sustainability),
            entity.licencesCertificates,
            entity.customLicencesCertificates
        ].reduce((sum, arr) => sum + (arr ? arr.length : 0), (entity.sustainability || []).includes('Fair Transport') ? 1 : 0) : 0
    ), [entity]);

    React.useEffect(() => {
        if (!entity || !tenantProfile) return;
        if (entity.isMyJob) return;
        if (entityMetadataLength === 0) return;

        const entityVehicleTypes = entity.vehicleTypes || [];
        const entityEquipment = entity.equipment || [];
        const entitySustainability = filterSustainabilityWithoutFairTransport(entity.sustainability) || [];
        const entityLicencesCertificates = entity.licencesCertificates || [];
        const entityCustomLicencesCertificates = entity.customLicencesCertificates || [];

        const tenantVehicleTypes = tenantProfile.vehicleTypes || [];
        const tenantEquipment = tenantProfile.equipment || [];
        const tenantSustainability = tenantProfile.sustainability || [];
        const tenantLicencesCertificates = tenantProfile.licencesCertificates || [];
        const tenantCustomLicencesCertificatesNormalized = (tenantProfile.customLicencesCertificates || []).map((lic) => lic.toLowerCase().trim());
        const mustBeFairTransport = (entity.sustainability || []).includes('Fair Transport');
        const tenantIsFairtransportMember = isTenantFairtransportMember(tenantProfile.fairtransportMembership);

        setMustBeFairtransport(mustBeFairTransport);

        const vehicleTypesMatch = entityVehicleTypes.map((veh) => tenantVehicleTypes.includes(veh) ? { name: veh, has: true } : { name: veh, has: false });

        const equipmentMatch = entityEquipment.map((eq) => tenantEquipment.includes(eq) ? { name: eq, has: true } : { name: eq, has: false });

        const sustainabilityMatch = entitySustainability.map((sus) => tenantSustainability.includes(sus) ? { name: sus, has: true } : { name: sus, has: false });

        const licencesCertificatesMatch = entityLicencesCertificates.map((lic) => tenantLicencesCertificates.includes(lic) ? { name: lic, has: true } : { name: lic, has: false });

        const customLicencesCertificatesMatch = entityCustomLicencesCertificates.map((lic) => tenantCustomLicencesCertificatesNormalized.includes(lic.toLowerCase().trim()) ? { name: lic, has: true } : { name: lic, has: false });

        const allRequirementsMet = [...vehicleTypesMatch, ...equipmentMatch, ...sustainabilityMatch, ...licencesCertificatesMatch, ...customLicencesCertificatesMatch].every((match) => match.has) && (mustBeFairTransport ? tenantIsFairtransportMember : true);

        setAllRequirementsMet(allRequirementsMet);

        setMatchingMetadata({
            vehicleTypesMatch,
            equipmentMatch,
            sustainabilityMatch,
            licencesCertificatesMatch,
            customLicencesCertificatesMatch,
            fairtransportMatch: !mustBeFairTransport || tenantProfile.isFairtransportMember
        });
    }, [entity, tenantProfile, entityMetadataLength]);

    return entity ? (
        <Paper
            sx={{
                position: 'absolute',
                bottom: '20px',
                left: '50%',
                transform: 'translateX(-50%)',
                minWidth: { xs: 0, md: '520px' },
                maxWidth: '1200px',
                width: 'calc(100% - 40px)',
                p: 1,
                pb: 0
            }}
        >
            <Grid
                container
                justifyContent="center"
                // alignItems="flex-start"
                spacing={2}
            // sx={{ p: 2 }}
            >
                <Grid item xs>
                    <SectionDivider
                        label={jobTitle}
                        marginTop={-2}
                        marginBottom={-1}
                    />
                    <EntityDriveLine entity={entity} />

                    {entity.publicDescription && (
                        <Typography variant="body2" display="block" align="center" sx={{ mt: 1 }}>
                            {entity.publicDescription}
                        </Typography>
                    )}

                    <Numbers
                        small
                        data={[
                            { title: 'Totalvikt [kg]', value: entity.grossWeight },
                            { title: 'FDR-vikt [kg]', value: entity.chargeableWeight },
                            { title: 'Volym [m³]', value: entity.volume },
                            { title: 'Flakmeter', value: entity.loadingMeters },
                            { title: 'Pallplatser', value: entity.palletPlaces },
                            { title: 'Pallar', value: entity.pallets }
                        ]}
                    />
                    {entityMetadataLength > 0 && (
                        <>
                            <SectionDivider
                                label={(
                                    <>
                                        {`krav (${entityMetadataLength})`}
                                        {metaDataCollapsed ? <ExpandMore /> : <ExpandLess />}
                                    </>
                                )}
                                marginTop={1}
                                marginBottom={0}
                                color={metaDataCollapsed && !meetsAllRequirements ? 'error' : 'inherit'}
                                onClick={() => setMetaDataCollapsed(!metaDataCollapsed)}
                            />
                            {!metaDataCollapsed && (
                                <>
                                    {mustBeFairtransport && (
                                        <Grid item xs={12} p={2} alignItems="center">
                                            <Box sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                columnGap: 1
                                            }}
                                            >
                                                <Tooltip
                                                    arrow
                                                    title={!matchingMetadata.fairtransportMatch
                                                        ? 'Ditt företag är inte certifierat av Fair Transport enligt våra uppgifter. Stämmer inte detta - kontakta vår support.'
                                                        : ''}
                                                >
                                                    <FairTransportEmblem style={{ height: 25, width: 25 }} color={matchingMetadata.fairtransportMatch ? 'success' : 'error'} />
                                                </Tooltip>
                                                <Typography variant="body2" component="span" sx={{ color: matchingMetadata.fairtransportMatch ? 'success.main' : 'error.main', fontWeight: 300 }}>
                                                    Kräver certifiering av Fair Transport
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    )}
                                    <Grid item xs={12}>
                                        <Grid
                                            container
                                            rowSpacing={1}
                                            columnSpacing={0}
                                            alignItems="flex-start"
                                        >
                                            {(entity.vehicleTypes || []).length > 0 && (
                                                <RequirementsSection
                                                    label="Fordonstyper"
                                                    entityMetadata={entity.vehicleTypes || []}
                                                    requirementsMatches={matchingMetadata.vehicleTypesMatch}
                                                    icon={<LocalShipping fontSize="small" />}
                                                    isMyJob={entity.isMyJob}
                                                />
                                            )}
                                            {(entity.equipment || []).length > 0 && (
                                                <RequirementsSection
                                                    label="Utrustning"
                                                    entityMetadata={entity.equipment || []}
                                                    requirementsMatches={matchingMetadata.equipmentMatch}
                                                    icon={<Construction fontSize="small" />}
                                                    isMyJob={entity.isMyJob}
                                                />
                                            )}
                                            {filterSustainabilityWithoutFairTransport(entity.sustainability).length > 0 && (
                                                <RequirementsSection
                                                    label="Hållbarhet"
                                                    entityMetadata={filterSustainabilityWithoutFairTransport(entity.sustainability)}
                                                    requirementsMatches={matchingMetadata.sustainabilityMatch}
                                                    icon={<Forest fontSize="small" />}
                                                    isMyJob={entity.isMyJob}
                                                />
                                            )}
                                            {[
                                                ...(entity.licencesCertificates || []),
                                                ...(entity.customLicencesCertificates || [])
                                            ].length > 0 && (
                                                <RequirementsSection
                                                    label="Licenser & certifikat"
                                                    entityMetadata={[
                                                        ...(entity.licencesCertificates || []),
                                                        ...(entity.customLicencesCertificates || [])
                                                    ]}
                                                    requirementsMatches={[
                                                        ...matchingMetadata.licencesCertificatesMatch,
                                                        ...matchingMetadata.customLicencesCertificatesMatch
                                                    ]}
                                                    icon={<CardMembership fontSize="small" />}
                                                    isMyJob={entity.isMyJob}
                                                />
                                            )}
                                        </Grid>
                                    </Grid>
                                </>
                            )}
                        </>
                    )}
                    <Divider sx={{ marginTop: 1, marginBottom: 1 }} />
                    <Grid item xs={12} my={1}>
                        <Typography variant="caption" display="block" align="center">
                            {getPublishedText({ entity, handleNavigateToCompanyProfile })}
                        </Typography>
                    </Grid>
                </Grid>

                <Grid item sx={{ minWidth: 200, maxWidth: 410 }}>
                    <Box
                        sx={{
                            p: 2,
                            pt: 1,
                            mb: 1,
                            borderRadius: 2,
                            backgroundColor: '#F5F5F5'
                        }}
                    >
                        {entity.type === 'LL' && !entity.isMyJob && (
                            <>
                                <Offer
                                    order={entity}
                                    offer={entity.offer}
                                    refetch={refetch}
                                    missingRequirements={entity.missingRequirements}
                                />
                                <Divider sx={{ mt: 1 }} />
                                <OfferChatButton
                                    offer={entity.offer}
                                    orderId={entity.id}
                                    canWrite={entity.isOpen}
                                    refetch={refetch}
                                    missingRequirements={entity.missingRequirements}
                                    recipient={entity.tenantName || '[Dold uppdragsgivare]'}
                                    sx={{ width: '100%', mt: 1 }}
                                />
                            </>
                        )}
                        {entity.type === 'LL' && entity.isMyJob && (
                            <>
                                <Grid item xs>
                                    <Typography variant="h6" color="primary">
                                        Detaljer
                                    </Typography>
                                </Grid>
                                <Grid item mt={1}>
                                    <Chip
                                        icon={<Paid />}
                                        label={
                                            entity.listPrice
                                                ? `Riktpris ${entity.listPrice} kr`
                                                : 'Saknar riktpris'
                                        }
                                    />
                                </Grid>
                                <Grid item mt={1}>
                                    <Chip
                                        icon={<Info />}
                                        label={`Besvaras senast ${getFormattedOrderExpiryDate(entity)}`}
                                    />
                                </Grid>
                                <Grid item mt={2}>
                                    <Button
                                        variant="contained"
                                        size="large"
                                        fullWidth
                                        onClick={() => navigate(`/orders/${entity.id}`)}
                                    >
                                        Gå till uppdraget
                                    </Button>
                                </Grid>
                            </>
                        )}
                        {entity.type === 'CAP' && (
                            <CapacitySuggestion capacity={entity} refetch={refetch} />
                        )}
                        {entity.type === 'DHL' && (
                            <DhlDetails entity={entity} />
                        )}
                    </Box>
                </Grid>
            </Grid>
        </Paper>
    ) : null;
}
